import React, { useEffect, useState } from 'react'
import { isNull, omit } from 'lodash'
import ModulePanel from '../../../../../ui/ModulePanel/ModulePanel'
import Button from '../../../../../ui/Button/Button'
import Spinner from '../../../../../ui/Spinner/Spinner'
import StatusMessage, { useStatusMessage } from '../../../../../ui/StatusMessage/StatusMessage'
import DeletePopUp from '../../../../../ui/PopUp/DeletePopUp/DeletePopUp'
import ModuleSpoiler from '../../../../../ui/ModuleSpoiler/ModuleSpoiler'
import getDoc from '../../../../../utils/db/getDoc'
import { createForm } from '../../../../../utils/newforms/createForm'
import getFormValues from '../../../../../utils/newforms/getFormValues'
import FormRender from '../../../../../utils/newforms/render/FormRender'
import getFieldRenderObject from '../../../../../utils/newforms/render/getFieldRenderObject'
import syncForm from '../../../../../utils/newforms/changed/syncForm'
import PhotoManager from '../../../../PhotoManager/PhotoManager'
import uploadPhotos from '../../../../../_deprecated/Nauka/NaukaManager/functions/uploadPhotos'
import {
  CATALOG_TAXONOMY_PHOTOS_STORAGE_PATH,
  CATALOG_TAXONOMY_SEO_OG_STORAGE_PATH,
  FULL_LANDING_SEO_FIELDS,
  FullLandingSeoForm,
  buildSeoDotPathUpdate,
  deleteSeoOgStorageFile,
  resolveOgImageForSave,
  seoFormDefaultsFromDoc,
} from '../../../seo'
import CatalogTaxonomyNodeSeoSection from '../CatalogTaxonomyNodeSeoSection'
import CatalogTaxonomySystemDataCard from '../CatalogTaxonomySystemDataCard'
import CatalogTaxonomyStatusLine from '../CatalogTaxonomyStatusLine'
import {
  archiveCatalogTaxonomyNode,
  createCatalogTaxonomyNode,
  deleteCatalogTaxonomySubtree,
  restoreCatalogTaxonomyNode,
  runCatalogTaxonomyDeletePreflight,
  saveCatalogTaxonomySeo,
  subscribeCatalogTaxonomySiblings,
  updateCatalogTaxonomyNode,
} from '../catalogTaxonomyApi'
import {
  CATALOG_TAXONOMY_COLLECTION,
  NODE_TYPE,
  editorTitleForNodeType,
} from '../catalogTaxonomyConstants'
import { computeEffectiveState } from '../catalogTaxonomyEffectiveState'
import { formatSubtreeDeleteCountsMessage } from '../catalogTaxonomySubtree'
import { slugifyCatalogTaxonomyName } from '../catalogTaxonomySlug'
import styles from './CatalogTaxonomyNodeEditor.module.scss'

class NodeIdentityForm {
  constructor() {
    this.name = {
      field: {
        fieldId: 'name',
        fieldType: 'input',
        label: 'Назва',
        required: true,
        value: '',
      },
      render: getFieldRenderObject(),
    }
    this.fullName = {
      field: {
        fieldId: 'fullName',
        fieldType: 'input',
        label: 'Повна назва',
        required: false,
        value: '',
      },
      render: getFieldRenderObject(),
    }
    this.slug = {
      field: {
        fieldId: 'slug',
        fieldType: 'input',
        label: 'Slug',
        required: true,
        value: '',
      },
      render: getFieldRenderObject(),
    }
    this.photos = {
      field: { fieldId: 'photos', value: [] },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.isActive = {
      field: {
        fieldId: 'isActive',
        fieldType: 'checkbox',
        label: 'Активний',
        required: false,
        value: false,
      },
      render: getFieldRenderObject(),
    }
    this.showInNavigation = {
      field: {
        fieldId: 'showInNavigation',
        fieldType: 'checkbox',
        label: 'Показувати в навігації',
        required: false,
        value: true,
      },
      render: getFieldRenderObject(),
    }
  }
}

function CatalogTaxonomyNodeEditor({
  mode, // 'edit' | 'create'
  nodeId,
  createType,
  createParentId,
  createParentNode,
  createWarning = null,
  ancestors = [],
  onCreated,
  onCloseCreate,
  onDeleted,
}) {
  const [node, setNode] = useState(null)
  const [siblings, setSiblings] = useState([])
  const [form, setForm] = useState(null)
  const [seoForm, setSeoForm] = useState(null)
  const [ogImage, setOgImage] = useState(null)
  const [previousOgImage, setPreviousOgImage] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadState, setLoadState] = useState('idle') // idle | loading | ready | missing
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletePreflight, setDeletePreflight] = useState(null)
  const [statusMessage, setStatusMessage] = useStatusMessage()

  const isCreate = mode === 'create'
  const effectiveParentId = isCreate
    ? createType === NODE_TYPE.GLOBAL
      ? null
      : createParentId
    : node?.parentId ?? null

  const effective = !isCreate && node ? computeEffectiveState(node, ancestors) : null
  const isBranchDelete = node && node.type !== NODE_TYPE.SUBCATEGORY
  const deleteCtaTitle = isBranchDelete ? 'Видалити гілку' : 'Видалити'

  useEffect(() => {
    if (!statusMessage.show) return undefined
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return undefined
  }, [statusMessage.show, statusMessage.message, statusMessage.type])

  useEffect(() => {
    if (isCreate) {
      setNode(null)
      setDeletePreflight(null)
      setLoadState('ready')
      setForm(
        createForm({
          formPattern: new NodeIdentityForm(),
          formData: {
            name: '',
            fullName: '',
            slug: '',
            photos: [],
            isActive: false,
            showInNavigation: true,
          },
        })
      )
      setSeoForm(null)
      setOgImage(null)
      setPreviousOgImage(null)
      return undefined
    }

    if (!nodeId) {
      setNode(null)
      setForm(null)
      setSeoForm(null)
      setDeletePreflight(null)
      setLoadState('idle')
      return undefined
    }

    let cancelled = false
    setForm(null)
    setSeoForm(null)
    setNode(null)
    setDeletePreflight(null)
    setLoadState('loading')

    getDoc({ path: CATALOG_TAXONOMY_COLLECTION, docId: nodeId, docIdName: 'id' }).then((data) => {
      if (cancelled) return
      if (!data) {
        setLoadState('missing')
        return
      }
      setNode(data)
      const seoDefaults = seoFormDefaultsFromDoc(data.seo, { fields: FULL_LANDING_SEO_FIELDS })
      setForm(
        createForm({
          formPattern: new NodeIdentityForm(),
          formData: data,
        })
      )
      setSeoForm(
        createForm({
          formPattern: new FullLandingSeoForm(),
          formData: seoDefaults,
        })
      )
      setOgImage(seoDefaults.ogImage || null)
      setPreviousOgImage(seoDefaults.ogImage || null)
      setLoadState('ready')
    })

    return () => {
      cancelled = true
    }
  }, [isCreate, nodeId, createType, createParentId])

  useEffect(() => {
    if (effectiveParentId === undefined && !isCreate && !node) return undefined
    const parentForQuery = isCreate ? effectiveParentId : node?.parentId
    if (parentForQuery === undefined && !isCreate) return undefined

    return subscribeCatalogTaxonomySiblings(isCreate ? effectiveParentId : node?.parentId ?? null, setSiblings)
  }, [isCreate, effectiveParentId, node?.parentId, node?.id])

  const onRegenerateSlug = () => {
    if (!form) return
    const values = getFormValues({ form })
    const nextSlug = slugifyCatalogTaxonomyName(values.name)
    setForm(
      createForm({
        formPattern: new NodeIdentityForm(),
        formData: { ...values, slug: nextSlug },
      })
    )
  }

  const onCreateSubmit = async () => {
    if (!form) return
    const values = getFormValues({ form })
    setIsLoading(true)
    setStatusMessage({ show: false })
    try {
      const id = await createCatalogTaxonomyNode({
        type: createType,
        parentId: createParentId,
        parentNode: createParentNode,
        name: values.name,
        fullName: values.fullName,
        slug: values.slug || slugifyCatalogTaxonomyName(values.name),
        siblings,
        isActive: values.isActive,
        showInNavigation: values.showInNavigation,
      })
      setStatusMessage({
        show: true,
        type: 'success',
        message: 'Створено',
        closeAfter: 3000,
      })
      if (onCreated) onCreated(id)
    } catch (err) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: err.message || 'Помилка створення',
        closeAfter: 5000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const onSave = async () => {
    if (!node || !form || !seoForm) return
    setIsLoading(true)
    setStatusMessage({ show: false })

    let uploadedOrphan = null
    try {
      const values = getFormValues({ form })
      await updateCatalogTaxonomyNode({
        nodeId: node.id,
        previousNode: node,
        siblings,
        patch: {
          name: values.name,
          fullName: values.fullName,
          slug: values.slug,
          isActive: values.isActive,
          showInNavigation: values.showInNavigation,
          photos: (values.photos || []).filter((p) => !p.needUpload).map((p) => omit(p, ['file', 'fileUrl'])),
        },
      })

      await uploadPhotos({
        photos: values.photos || [],
        docId: node.id,
        docPath: CATALOG_TAXONOMY_COLLECTION,
        storagePath: CATALOG_TAXONOMY_PHOTOS_STORAGE_PATH,
      })

      const seoValues = getFormValues({ form: seoForm })
      const { nextOgImage, uploadedOrphan: orphan, previousToDelete } = await resolveOgImageForSave({
        docId: node.id,
        localOgImage: ogImage,
        previousOgImage,
        storagePath: CATALOG_TAXONOMY_SEO_OG_STORAGE_PATH,
      })
      uploadedOrphan = orphan

      const seoPayload = buildSeoDotPathUpdate(
        { ...seoValues, ogImage: nextOgImage },
        { fields: FULL_LANDING_SEO_FIELDS }
      )
      await saveCatalogTaxonomySeo({ nodeId: node.id, seoDotPathUpdate: seoPayload })

      if (previousToDelete) {
        await deleteSeoOgStorageFile({
          docId: node.id,
          media: previousToDelete,
          storagePath: CATALOG_TAXONOMY_SEO_OG_STORAGE_PATH,
        })
      }

      setPreviousOgImage(nextOgImage)
      setOgImage(nextOgImage)
      setStatusMessage({
        show: true,
        type: 'success',
        message: 'Збережено',
        closeAfter: 3000,
      })

      const refreshed = await getDoc({ path: CATALOG_TAXONOMY_COLLECTION, docId: node.id, docIdName: 'id' })
      if (refreshed) {
        setNode(refreshed)
        setForm(
          createForm({
            formPattern: new NodeIdentityForm(),
            formData: refreshed,
          })
        )
      }
    } catch (err) {
      if (uploadedOrphan) {
        await deleteSeoOgStorageFile({
          docId: node.id,
          media: uploadedOrphan,
          storagePath: CATALOG_TAXONOMY_SEO_OG_STORAGE_PATH,
        })
      }
      setStatusMessage({
        show: true,
        type: 'fail',
        message: err.message || 'Помилка збереження',
        closeAfter: 5000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const onArchive = async () => {
    if (!node) return
    setIsLoading(true)
    try {
      await archiveCatalogTaxonomyNode({ node, siblings })
      setShowArchiveConfirm(false)
      const refreshed = await getDoc({ path: CATALOG_TAXONOMY_COLLECTION, docId: node.id, docIdName: 'id' })
      if (refreshed) setNode(refreshed)
      setStatusMessage({
        show: true,
        type: 'success',
        message: 'Архівовано',
        closeAfter: 3000,
      })
    } catch (err) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: err.message || 'Помилка архівування',
        closeAfter: 5000,
      })
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const onRestore = async () => {
    if (!node) return
    setIsLoading(true)
    try {
      await restoreCatalogTaxonomyNode({ node, siblings })
      setShowRestoreConfirm(false)
      const refreshed = await getDoc({ path: CATALOG_TAXONOMY_COLLECTION, docId: node.id, docIdName: 'id' })
      if (refreshed) setNode(refreshed)
      setStatusMessage({
        show: true,
        type: 'success',
        message: 'Відновлено. Увімкніть активність і навігацію за потреби.',
        closeAfter: 4000,
      })
    } catch (err) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: err.message || 'Помилка відновлення',
        closeAfter: 5000,
      })
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const onDelete = async () => {
    if (!node || !deletePreflight?.ok) return
    setIsLoading(true)
    try {
      const result = await deleteCatalogTaxonomySubtree({
        root: node,
        siblingsOfRoot: siblings,
        preflight: deletePreflight,
      })
      setShowDeleteConfirm(false)
      setDeletePreflight(null)

      const storageFailed = result.storage?.failed?.length > 0
      setStatusMessage({
        show: true,
        type: storageFailed ? 'fail' : 'success',
        message: storageFailed
          ? 'Документи видалено; частину файлів Storage не вдалося прибрати.'
          : 'Видалено',
        closeAfter: storageFailed ? 6000 : 3000,
      })
      if (onDeleted) onDeleted(node)
    } catch (err) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: err.message || 'Помилка видалення',
        closeAfter: 5000,
      })
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const openDeleteConfirm = async () => {
    if (!node) return
    setIsLoading(true)
    setStatusMessage({ show: false })
    try {
      const preflight = await runCatalogTaxonomyDeletePreflight({
        root: node,
        siblingsOfRoot: siblings,
      })
      setDeletePreflight(preflight)
      if (!preflight.ok) {
        const first = preflight.blockers[0]
        setStatusMessage({
          show: true,
          type: 'fail',
          message: first?.message || 'Видалення заборонено.',
          closeAfter: 6000,
        })
        return
      }
      setShowDeleteConfirm(true)
    } catch (err) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: err.message || 'Не вдалося підготувати видалення',
        closeAfter: 5000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (!isCreate && !nodeId) {
    return (
      <ModulePanel title="Редактор">
        <p className={styles.hint}>Оберіть елемент у дереві або створіть новий.</p>
      </ModulePanel>
    )
  }

  if (!isCreate && loadState === 'missing') {
    return (
      <ModulePanel title="Редактор">
        <p className={styles.hint}>Вузол не знайдено або його вже видалено. Оберіть інший елемент у дереві.</p>
      </ModulePanel>
    )
  }

  if (!isCreate && (loadState === 'loading' || isNull(form) || !node)) {
    return (
      <ModulePanel title="Редактор">
        <Spinner type="module" />
      </ModulePanel>
    )
  }

  if (isCreate && isNull(form)) {
    return (
      <ModulePanel title="Новий елемент">
        <Spinner type="module" />
      </ModulePanel>
    )
  }

  const title = isCreate
    ? editorTitleForNodeType(createType, 'create')
    : editorTitleForNodeType(node?.type, 'edit')

  return (
    <div className={styles.manager}>
      {statusMessage.show && (
        <StatusMessage
          className="Site-StatusMessage"
          type={statusMessage.type}
          message={statusMessage.message}
        />
      )}

      <ModulePanel title={title}>
        <div className={styles.root}>
          {isCreate && createWarning ? <p className={styles.inheritedBanner}>{createWarning}</p> : null}

          {!isCreate && node && effective && !effective.effectiveActive ? (
            <div className={styles.inheritedBanner}>
              <CatalogTaxonomyStatusLine node={node} ancestors={ancestors} includeAncestorHiddenNav />
              <p className={styles.inheritedHint}>
                Effective: архів={String(effective.effectiveArchived)}, активний=
                {String(effective.effectiveActive)}, навігація={String(effective.effectiveNavigation)}.
                Дочірні документи зберігають власні прапорці; доступність рахується по ланцюгу батьків.
              </p>
            </div>
          ) : null}

          {!isCreate && node && <CatalogTaxonomySystemDataCard node={node} seoForm={seoForm} />}

          <div className={styles.fields}>
            <div className={styles.identityRow}>
              <div className={styles.field}>
                <FormRender sections={[{ fields: ['name'] }]} form={form} setForm={setForm} errors={false} />
              </div>
              <div className={styles.field}>
                <FormRender sections={[{ fields: ['fullName'] }]} form={form} setForm={setForm} errors={false} />
              </div>
              <div className={styles.field}>
                <div className={styles.slugField}>
                  <FormRender sections={[{ fields: ['slug'] }]} form={form} setForm={setForm} errors={false} />
                  <Button
                    className={styles.slugRegen}
                    theme="circle"
                    fill="white"
                    icon="sync"
                    tooltip="Перегенерувати slug з назви"
                    onClick={onRegenerateSlug}
                  />
                </div>
              </div>
            </div>
            {!isCreate && node?.id ? (
              <div className={styles.photos}>
                <p className={styles.photosTitle}>Фото</p>
                <PhotoManager
                  projectId={node.id}
                  projectPath={CATALOG_TAXONOMY_COLLECTION}
                  formData={form ? form.photos.values : null}
                  syncState={(moduleState) =>
                    syncForm({
                      propIdentifier: 'photos',
                      form,
                      setForm,
                      newForm: moduleState,
                    })
                  }
                />
              </div>
            ) : null}
            <div className={styles.flags}>
              <FormRender sections={[{ fields: ['isActive'] }]} form={form} setForm={setForm} errors={false} />
              <FormRender
                sections={[{ fields: ['showInNavigation'] }]}
                form={form}
                setForm={setForm}
                errors={false}
              />
            </div>
          </div>

          {!isCreate && (seoForm || node) && (
            <div className={styles.spoilerStack}>
              {seoForm && (
                <CatalogTaxonomyNodeSeoSection
                  form={seoForm}
                  setForm={setSeoForm}
                  ogImage={ogImage}
                  setOgImage={setOgImage}
                />
              )}

              {node && (
                <ModuleSpoiler title="Небезпечна зона" theme="danger" defaultExpanded={false}>
                  <div className={styles.dangerZone}>
                    <p className={styles.dangerHint}>
                      Архів приховує вузол логічно для всього subtree (документи дітей не змінюються). Видалення
                      гілки стирає вибраний вузол і всіх нащадків назавжди після перевірки.
                    </p>
                    <div className={styles.dangerActions}>
                      {node.isArchived ? (
                        <Button
                          className={styles.dangerBtnSolid}
                          theme="solid"
                          title="Відновити з архіву"
                          size={32}
                          onClick={() => setShowRestoreConfirm(true)}
                          isLoading={isLoading}
                        />
                      ) : (
                        <Button
                          className={styles.dangerBtnSolid}
                          theme="solid"
                          title="Архівувати"
                          size={32}
                          onClick={() => setShowArchiveConfirm(true)}
                          isLoading={isLoading}
                        />
                      )}
                      <Button
                        className={styles.dangerBtnOutline}
                        theme="bounded"
                        title={deleteCtaTitle}
                        size={32}
                        onClick={openDeleteConfirm}
                        isLoading={isLoading}
                      />
                    </div>
                  </div>
                </ModuleSpoiler>
              )}
            </div>
          )}
        </div>
      </ModulePanel>

      <div className={styles.actions}>
        {isCreate ? (
          <>
            <Button
              theme="solid"
              fill="accent"
              title="Створити"
              size={32}
              onClick={onCreateSubmit}
              isLoading={isLoading}
            />
            <Button theme="transparent" title="Скасувати" size={32} onClick={onCloseCreate} />
          </>
        ) : (
          <Button
            theme="solid"
            fill="accent"
            title="Зберегти"
            size={32}
            onClick={onSave}
            isLoading={isLoading}
          />
        )}
      </div>

      {showArchiveConfirm && (
        <DeletePopUp
          title="Архівувати?"
          warningMessage={
            <>
              Вузол <span className="Bolder">«{node?.name || '—'}»</span> буде архівовано. Дочірні категорії не
              змінюються, але стануть логічно недоступними через батьківську категорію. Особиста «архівація» дочірніх категорій
              зберігається. Slug залишиться зарезервованим. Опція «Показувати в навігації» на цьому вузлі зберігається.
            </>
          }
          submitTitle="Архівувати"
          submitFill="accent"
          closePopUp={() => setShowArchiveConfirm(false)}
          submitDelete={onArchive}
        />
      )}

      {showRestoreConfirm && (
        <DeletePopUp
          title="Відновити з архіву?"
          warningMessage={
            <>
              Вузол <span className="Bolder">«{node?.name || '—'}»</span> повернеться в основний список. Прапорці
              дітей не змінюються; доступність перерахується по ланцюгу.
            </>
          }
          submitTitle="Відновити"
          submitFill="accent"
          closePopUp={() => setShowRestoreConfirm(false)}
          submitDelete={onRestore}
        />
      )}

      {showDeleteConfirm && deletePreflight?.ok && (
        <DeletePopUp
          title={isBranchDelete ? 'Видалити гілку?' : 'Видалити вузол?'}
          warningMessage={
            <>
              Буде назавжди видалено:
              <br />
              {formatSubtreeDeleteCountsMessage(deletePreflight.counts).map((line) => (
                <React.Fragment key={line}>
                  - {line}
                  <br />
                </React.Fragment>
              ))}
              (Всього: {deletePreflight.counts.subtreeDocuments} документів, batch writes:{' '}
              {deletePreflight.batch.totalWrites}.)
              <br />
              Дію неможливо скасувати. Після Firestore буде спроба очистити photos/OG у Storage.
            </>
          }
          closePopUp={() => {
            setShowDeleteConfirm(false)
            setDeletePreflight(null)
          }}
          submitDelete={onDelete}
        />
      )}
    </div>
  )
}

export default CatalogTaxonomyNodeEditor
